<template>
  <div class="relative w-full h-full bg-black overflow-hidden">
    <video
        ref="video"
        class="absolute inset-0 w-full h-full object-cover"
        playsinline
        muted
        autoplay
    ></video>

    <div class="absolute inset-0 flex items-center justify-center pointer-events-none">
      <div class="w-64 h-40 border-2 border-white/80 rounded-2xl" style="box-shadow: 0 0 0 2000px rgba(0,0,0,0.35);"></div>
    </div>

    <div
        v-if="cameraState === 'denied' || cameraState === 'unsupported'"
        class="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-black/85 text-white text-center px-6"
    >
      <i class="fas fa-video-slash text-3xl"></i>
      <p class="text-sm leading-6">
        {{
          cameraState === 'denied'
              ? 'دسترسی به دوربین رد شد. از ورودی دستی استفاده کنید یا دسترسی دوربین را در تنظیمات مرورگر فعال کنید.'
              : 'دوربین در این مرورگر در دسترس نیست. کد را به‌صورت دستی وارد کنید.'
        }}
      </p>
    </div>

    <button
        v-if="torchAvailable"
        type="button"
        @click="toggleTorch"
        class="absolute top-3 left-3 w-11 h-11 rounded-full bg-black/50 flex items-center justify-center"
    >
      <i class="fas fa-bolt" :class="torchOn ? 'text-yellow-300' : 'text-white'"></i>
    </button>
  </div>
</template>

<script>
const DUPLICATE_SCAN_COOLDOWN_MS = 1500;
const NATIVE_DECODE_INTERVAL_MS = 150;
const BARCODE_FORMATS = ['ean_13', 'ean_8', 'upc_a', 'upc_e', 'code_128', 'code_39', 'qr_code'];

export default {
  name: 'BarcodeScanner',
  props: {
    active: {
      type: Boolean,
      default: false,
    },
    formats: {
      type: Array,
      default: () => BARCODE_FORMATS,
    },
  },
  data() {
    return {
      cameraState: 'unknown', // unknown | granted | denied | unsupported
      torchAvailable: false,
      torchOn: false,
    };
  },
  watch: {
    active(val) {
      if (val) {
        this.start();
      } else {
        this.stop();
      }
    },
  },
  mounted() {
    if (this.active) {
      this.start();
    }
  },
  beforeDestroy() {
    this.stop();
  },
  methods: {
    async start() {
      if (this._starting || this._running) return;
      this._starting = true;

      try {
        this._stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: { ideal: 'environment' },
            // iOS in particular otherwise sometimes hands us the ultra-wide lens
            // (poor close-up focus for a small barcode) or leaves focus locked
            // far away; these are non-fatal hints — unsupported ones are ignored
            // rather than rejecting the whole getUserMedia call.
            width: { ideal: 1280 },
            height: { ideal: 720 },
            advanced: [{ focusMode: 'continuous' }],
          },
          audio: false,
        });
      } catch (err) {
        this._starting = false;
        this.handleCameraError(err);
        return;
      }

      this.cameraState = 'granted';

      const track = this._stream.getVideoTracks()[0];
      this._track = track;
      try {
        // Re-assert continuous autofocus directly on the track — some browsers
        // (notably iOS Safari) only honor focus-related constraints here,
        // not in the initial getUserMedia() request.
        await track.applyConstraints({ advanced: [{ focusMode: 'continuous' }] });
      } catch (e) {
        // focusMode not supported on this browser/device — camera still works,
        // just without a hint to refocus continuously.
      }
      try {
        const caps = track && track.getCapabilities ? track.getCapabilities() : {};
        this.torchAvailable = !!(caps && caps.torch);
      } catch (e) {
        this.torchAvailable = false;
      }

      const video = this.$refs.video;
      video.srcObject = this._stream;
      try {
        await video.play();
      } catch (e) {
        // ignore autoplay race, decode loop still attaches once frames arrive
      }

      this._running = true;
      if ('BarcodeDetector' in window) {
        this.startNativeLoop(video);
      } else {
        await this.startZxingLoop(this._stream, video);
      }

      this._starting = false;
    },

    startNativeLoop(video) {
      let detector;
      try {
        detector = new window.BarcodeDetector({ formats: this.formats });
      } catch (e) {
        detector = new window.BarcodeDetector();
      }

      this._lastDecodeAt = 0;
      const loop = async (ts) => {
        if (!this._running) return;
        if (!this._lastDecodeAt || ts - this._lastDecodeAt > NATIVE_DECODE_INTERVAL_MS) {
          this._lastDecodeAt = ts;
          try {
            const codes = await detector.detect(video);
            if (codes && codes.length) {
              const allowed = codes.find(code => !code.format || this.formats.includes(code.format));
              if (allowed) this.emitDetected(allowed.rawValue);
            }
          } catch (e) {
            // transient per-frame decode error, keep looping
          }
        }
        this._rafId = requestAnimationFrame(loop);
      };
      this._rafId = requestAnimationFrame(loop);
    },

    async startZxingLoop(stream, video) {
      // This is the path every iOS browser takes (Safari never shipped
      // BarcodeDetector). Restricting to the formats we actually care about
      // and turning on TRY_HARDER meaningfully improves hit rate on a phone
      // camera that isn't perfectly focused, at the cost of a bit more CPU
      // per frame — worth it since detection was the actual complaint.
      try {
        const [{ BrowserMultiFormatReader }, { BarcodeFormat, DecodeHintType }] = await Promise.all([
          import('@zxing/browser'),
          import('@zxing/library'),
        ]);

        const hints = new Map();
        const zxingFormats = {
          ean_13: BarcodeFormat.EAN_13, ean_8: BarcodeFormat.EAN_8,
          upc_a: BarcodeFormat.UPC_A, upc_e: BarcodeFormat.UPC_E,
          code_128: BarcodeFormat.CODE_128, code_39: BarcodeFormat.CODE_39,
          qr_code: BarcodeFormat.QR_CODE,
        };
        hints.set(DecodeHintType.POSSIBLE_FORMATS, this.formats.map(format => zxingFormats[format]).filter(Boolean));
        hints.set(DecodeHintType.TRY_HARDER, true);

        this._zxingReader = new BrowserMultiFormatReader(hints, { delayBetweenScanAttempts: 100 });
        this._zxingControls = await this._zxingReader.decodeFromStream(stream, video, (result) => {
          if (result) {
            this.emitDetected(result.getText());
          }
        });
      } catch (e) {
        this.cameraState = 'unsupported';
        this.$emit('unsupported');
      }
    },

    emitDetected(code) {
      const value = String(code || '').trim();
      if (!value) return;
      const now = Date.now();
      if (value === this._lastCode && now - (this._lastEmitAt || 0) < DUPLICATE_SCAN_COOLDOWN_MS) {
        return;
      }
      this._lastCode = value;
      this._lastEmitAt = now;
      this.$emit('detected', value);
    },

    handleCameraError(err) {
      const name = err && err.name;
      if (name === 'NotAllowedError' || name === 'SecurityError') {
        this.cameraState = 'denied';
        this.$emit('permission-denied');
      } else {
        this.cameraState = 'unsupported';
        this.$emit('unsupported');
      }
    },

    async toggleTorch() {
      if (!this._track || !this.torchAvailable) return;
      const nextState = !this.torchOn;
      try {
        await this._track.applyConstraints({ advanced: [{ torch: nextState }] });
        this.torchOn = nextState;
      } catch (e) {
        // torch toggle not actually supported despite capability flag, leave state unchanged
      }
    },

    stop() {
      this._running = false;

      if (this._rafId) {
        cancelAnimationFrame(this._rafId);
        this._rafId = null;
      }

      if (this._zxingControls) {
        try { this._zxingControls.stop(); } catch (e) { /* noop */ }
        this._zxingControls = null;
      }
      if (this._zxingReader) {
        try { this._zxingReader.reset(); } catch (e) { /* noop */ }
        this._zxingReader = null;
      }

      if (this._stream) {
        this._stream.getTracks().forEach((t) => t.stop());
        this._stream = null;
      }
      this._track = null;
      this.torchAvailable = false;
      this.torchOn = false;

      if (this.$refs.video) {
        this.$refs.video.srcObject = null;
      }
    },
  },
};
</script>
